import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointments, Appointment } from '../../crm-appointments/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getUpcomingRangeFromTimeRange } from '../contexts/DashboardFiltersContext';
import type { TimeRange } from './TimeRangeSelector';
import type { DashboardScope } from '../contexts/DashboardFiltersContext';

export interface UpcomingAppointmentsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  timeRange?: TimeRange;
  scope?: DashboardScope;
}

const UpcomingAppointments: React.FC<UpcomingAppointmentsProps> = ({
  limit: limitProp = 5,
  embedded,
  settings,
  timeRange = 'month',
  scope = 'myWork',
}) => {
  const limit = typeof settings?.limit === 'number' ? settings.limit : limitProp;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appointments, isLoading } = useAppointments();
  const { colors } = useTheme();
  const cardClass = embedded
    ? 'min-w-0 flex-1 flex flex-col min-h-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1';

  const { startDate, endDate } = useMemo(() => getUpcomingRangeFromTimeRange(timeRange), [timeRange]);
  const upcoming = (appointments || [])
    .filter((apt: Appointment) => {
      if (!apt.clientAppScheduleDateTime) return false;
      const aptDate = new Date(apt.clientAppScheduleDateTime);
      if (aptDate < startDate) return false;
      if (aptDate > endDate) return false;
      if (scope === 'myWork' && user?.id != null && apt.assignedTo !== user.id) return false;
      return true;
    })
    .sort((a: Appointment, b: Appointment) => {
      const dateA = new Date(a.clientAppScheduleDateTime || 0).getTime();
      const dateB = new Date(b.clientAppScheduleDateTime || 0).getTime();
      return dateA - dateB;
    })
    .slice(0, limit);

  const formatDateTime = (dateString?: string): { date: string; time: string; full: string } => {
    if (!dateString) {
      return { date: 'N/A', time: 'N/A', full: 'N/A' };
    }
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      full: date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
    };
  };

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>Upcoming Appointments</h2>
        <div className="space-y-2 sm:space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className={`flex items-center space-x-3 sm:space-x-4 pb-2 sm:pb-3 ${colors.border} border-b last:border-0`}>
              <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-lg ${colors.surfaceHover} animate-pulse flex-shrink-0`}></div>
              <div className="flex-1 min-w-0">
                <div className={`h-3 sm:h-4 ${colors.surfaceHover} animate-pulse rounded w-3/4 mb-1 sm:mb-2`}></div>
                <div className={`h-2.5 sm:h-3 ${colors.surfaceHover} animate-pulse rounded w-1/2`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>Upcoming Appointments</h2>
        <button
          onClick={() => navigate('/appointments')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {upcoming.length > 0 ? (
          upcoming.map((apt: Appointment) => {
            const dateTime = formatDateTime(apt.clientAppScheduleDateTime);
            const dateParts = dateTime.date.split(' ');
            return (
              <div
                key={apt.clientAppId}
                onClick={() => navigate(`/appointments/${apt.clientAppId}`)}
                className={`flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors ${colors.border} border-b border-gray-100 dark:border-gray-700/50 last:border-0`}
              >
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-200/70 dark:bg-teal-900/30 dark:text-teal-300 dark:ring-teal-800/50 flex flex-col items-center justify-center">
                    {dateParts.length >= 2 ? (
                      <>
                        <span className="text-[10px] sm:text-xs font-semibold">{dateParts[0]}</span>
                        <span className="text-xs sm:text-sm font-bold">{dateParts[1]}</span>
                      </>
                    ) : (
                      <span className="text-[10px] sm:text-xs font-semibold">N/A</span>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs sm:text-sm font-medium ${colors.text} truncate`}>
                    {apt.clientAppName || (apt.client?.clientName ? (apt.client.clientName.length > 20 ? apt.client.clientName.substring(0, 20) + '...' : apt.client.clientName) : 'Untitled Appointment')}
                  </p>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1">
                    <svg className={`w-3 h-3 sm:w-4 sm:h-4 ${colors.textMuted} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className={`text-[10px] sm:text-xs ${colors.textMuted} truncate`}>{dateTime.full}</p>
                  </div>
                  {apt.client?.clientName && (
                    <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-0.5 sm:mt-1 truncate`}>{apt.client.clientName.length > 20 ? apt.client.clientName.substring(0, 20) + '...' : apt.client.clientName}</p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className={`text-center py-8 ${colors.textMuted}`}>
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm sm:text-base">No upcoming appointments</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingAppointments;

